/**
 * Reusable hook for managing indicator hover state
 *
 * Eliminates repetitive hover handler code by providing a unified
 * hover state management pattern for patient context indicators.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const HOVER_DELAY = 200; // ms delay before closing popover

export interface UseIndicatorHoverReturn {
  isHovered: boolean;
  anchorEl: HTMLElement | null;
  indicatorRef: React.RefObject<HTMLDivElement>;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  handlePopoverEnter: () => void;
  handlePopoverLeave: () => void;
}

/**
 * Hook for managing hover state of patient context indicators
 *
 * @returns Hover state and handlers for indicator and popover
 */
export function useIndicatorHover(): UseIndicatorHoverReturn {
  const [isHovered, setIsHovered] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    if (indicatorRef.current) {
      setAnchorEl(indicatorRef.current);
    }
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
      setAnchorEl(null);
    }, HOVER_DELAY);
  }, []);

  const handlePopoverEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  }, []);

  const handlePopoverLeave = useCallback(() => {
    setIsHovered(false);
    setAnchorEl(null);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return {
    isHovered,
    anchorEl,
    indicatorRef,
    handleMouseEnter,
    handleMouseLeave,
    handlePopoverEnter,
    handlePopoverLeave,
  };
}

